import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as agent from '../agent'
import type * as common from '../common'
import type * as display from '../display'
import * as dms from '../dms'
import type * as relay from '../relay'

import type { Widget } from './widget'

/**
 * Represents something that gets executed based on user interactions
 *
 * For example, when user clicks a "Save" button, that might trigger an
 * {@link Action} that ultimately writes data to a database
 *
 * An {@link Action} has an {@link Action.Query} that produces an
 * {@link Action.Rendering}. {@link Action.Rendering} conveys what actually gets
 * executed through an {@link Action.Operation} and {@link Action.Payload}
 */
export interface Action
  extends common.Identified<Action.Id>,
    common.Aliased,
    common.Named,
    common.Described.Optionally {
  /**
   * A {@link dms.data.query.Query} associated with this {@link Action}, used to produce an
   * {@link Action.Rendering}
   */
  query: Action.Query
}
export namespace Action {
  /**
   * Identifies an {@link Action}
   */
  export type Id = string
  export namespace Id {
    export const isId = TypeChecker.isString.usingAlias('mosaic.Action.Id')
    export const assertIsId = TypeAsserter.Factory.from(isId)
  }

  /**
   * Can be used to trigger an {@link Action}
   */
  export interface Ref extends common.Named.Optionally, Confirmation.Option {
    /**
     * Represents the {@link Action} that is intended to be executed
     *
     * __NOTE:__ this could also be a {@link Action.Rendering} for a
     * {@link Action.Operation.ForUi}, in which case this {@link Action.Ref}
     * effectively represents an anonymous/inline {@link Action} rather than a
     * true, persistent {@link Action}
     */
    action:
      | common.FlexibleIdentifier<Id>
      | Action.Rendering<Action.Operation.ForUi>
    /**
     * __NOTE__: This will be ignored if {@link action} is an {@link Action.Rendering}
     */
    variables?: dms.data.query.Variable.Lookup

    /**
     * TODO:
     *
     * @example ...
     */
    skipLoading?: boolean
  }
  export namespace Ref {
    /**
     * TODO: Add explanation
     */
    export type UserInputVariableValue = `\${${string}}`
    export namespace UserInputVariableValue {
      export const isUserInputVariableValue: TypeChecker<UserInputVariableValue> =
        TypeChecker.Factory.custom(
          (val: any): val is UserInputVariableValue =>
            TypeChecker.isString(val) &&
            val.startsWith('${') &&
            val.endsWith('}'),
          { typeDescription: 'Action.Ref.UserInputVariableValue' },
        )

      /** TODO: */
      export function from(variable: string): UserInputVariableValue {
        return `\${${variable}}`
      }

      /**
       * Substitutes {@link UserInputVariableValue} variables in a
       * {@link dms.data.query.Variable.Lookup variablesLookup} by matching
       * values from the valueLookup object
       *
       * @example
       * // variablesLookup
       * { 'awardName': '${field:name}' }
       * // valueLookup
       * { '${field:name}': 'John Doe' }
       * // result after substitution
       * { 'awardName': 'John Doe' }
       */
      export function replaceAll(
        variablesLookup: dms.data.query.Variable.Lookup,
        valueLookup: Partial<Record<string, unknown>>,
      ): dms.data.query.Variable.Lookup {
        variablesLookup = { ...variablesLookup }
        for (const alias in variablesLookup) {
          const value = variablesLookup[alias]
          if (isUserInputVariableValue(value)) {
            variablesLookup[alias] = valueLookup[value]
          }
        }
        return variablesLookup
      }
    }

    /**
     * Represents an {@link Action.Ref} shown in the overflow menu, with
     * optional support for displaying an icon.
     */
    export type WithIcon = Action.Ref & {
      /**
       * The icon to display beside the action name
       *
       * If not Provided, no action will be displayed
       *
       * @default undefined
       */
      icon?: string
    }
  }

  /**
   * Can be used to confirm an {@link Action} before it is executed
   */
  export interface Confirmation {
    title?: string
    message?: string
    /**
     * When this is provided, the user must type this value into an input
     * before they are able to confirm
     */
    requiredInput?: string
  }
  export namespace Confirmation {
    export interface Option {
      /**
       * Requires that a user confirm through a confirmation dialog before
       * proceeding
       *
       * @default true
       */
      confirm?: boolean | Confirmation
    }
  }

  /**
   * @see {@link Action.query}
   */
  export type Query = dms.data.query.ForSingleton<Query.Result>
  export namespace Query {
    // NOTE: we don't really have a way to check if the query is a valid query
    // for a widget rendering, so we just check if it is a query in general
    export const isQuery = dms.data.query.Query.isQuery as TypeChecker<Query>
    export const assertIsQuery: TypeAsserter<Query> =
      TypeAsserter.Factory.from(isQuery)

    /**
     * Return type of a {@link Action.Query}
     *
     * @see {@link Action.Rendering}
     */
    export type Result = Partial<Action.Rendering>
  }

  /**
   * TODO: utilize {@link relay.Command}
   *
   *
   * Conveys what actually gets executed through {@link Rendering.operation} and
   * {@link Rendering.payload}
   *
   * __NOTE:__ this is a discriminated union using {@link Rendering.operation}
   *
   * @example
   * if (rendering.operation === ents.core.mosaic.Action.Operation.UiNavigate) {
   *   // rendering.payload is of type Payload<Operation.UiNavigate>
   * }
   */
  export type Rendering<TOp extends Operation = Operation> = TOp extends any
    ? {
        // NOTE: everything is optional so that we are forced to handle it
        /** @see {@link Operation} */
        operation?: TOp
        /** @see {@link Payload} */
        payload?: Payload<TOp>
      }
    : never
  export namespace Rendering {
    /**
     * An {@link Action.Rendering} with an {@link Operation.ForUi}
     */
    export type OfUiOperationsOnly = Rendering<Operation.ForUi>
  }

  /**
   * Represents the results of an executed {@link Action}
   *
   * Includes {@link Action.id} and {@link Action.alias} because only one needs
   * to be specified in {@link Action.Ref}, but you may want the other when
   * using the results
   */
  export interface ExecutionResult extends Pick<Action, 'id' | 'alias'> {
    executedInUi: readonly Rendering.OfUiOperationsOnly[]
  }

  /**
   * TODO: explain what this enum is, when it is used and why, as well as any examples
   *
   * TODO: add JSDoc comments to each enum value to explain it
   */
  export enum Operation {
    /**
     * Executes multiple {@link Operation}
     */
    Combine = 'combine',
    /**
     * TODO:
     */
    UiNavigate = 'ui.navigate',
    /**
     * TODO:
     */
    UiModal = 'ui.modal',
    /**
     * TODO:
     */
    UiSlideout = 'ui.slideout',
    /**
     * TODO:
     */
    UiNotify = 'ui.notify',
    /**
     * TODO:
     */
    UiSetVariables = 'ui.set.variables',
    /**
     * Opens the assistant slideout for one exact agent, optionally
     * authorizing it to retrieve a fixed set of knowledge-base records for
     * the duration of the session.
     */
    UiOpenAssistant = 'ui.open-assistant',
    /**
     * TODO:
     */
    DmsMutate = 'dms.mutate',
    /**
     * TODO: starts an approval request
     */
    ApprovalsRequest = 'approvals.request',
    /**
     * TODO: approves, rejects, or requests modification from an approval
     */
    ApprovalsRespond = 'approvals.respond',
    // TODO: how do we want to add comments to approvals, either from the
    // requester or reviewer, or maybe even someone else?
  }
  // Needs to be before namespace is defined
  const OPERATIONS: ReadonlySet<Operation> = new Set(
    Object.values(Operation) as Operation[],
  )
  export namespace Operation {
    export const ALL: readonly Operation[] = Array.from(OPERATIONS)
    export const isOperation = TypeChecker.Factory.oneOf(OPERATIONS)
    export const assertIsOperation: TypeAsserter<Operation> =
      TypeAsserter.Factory.from(isOperation)

    /**
     * An {@link Operation} that is executed in the user interface (UI)
     */
    export type ForUi = Extract<Operation, `${ForUiPrefix}${string}`>
    export namespace ForUi {
      export const isForUi: TypeChecker<ForUi> = TypeChecker.Factory.oneOf(
        ALL.filter((op): op is ForUi =>
          op.startsWith('ui.' satisfies ForUiPrefix),
        ),
      )
      export const assertIsForUi: TypeAsserter<ForUi> =
        TypeAsserter.Factory.from(isForUi)
    }
    type ForUiPrefix = 'ui.'
    /** Used to ensure {@link ForUiPrefix} is kept up-to-date */
    type ForUiPrefixIsValid = Satisfies<
      ForUi extends never
        ? `ERROR: the prefix of '${ForUiPrefix}' does not appear in any Operation`
        : 1,
      1
    >
  }

  /**
   * Contains necessary information to execute a given {@link Operation}
   */
  export type Payload<TOp extends Operation> = {
    [Operation.Combine]: readonly Action.Rendering<
      Exclude<Operation, Operation.Combine>
    >[]
    [Operation.UiModal]: Widget.Ref
    [Operation.UiSlideout]: Widget.Ref
    [Operation.UiSetVariables]: {
      variables: dms.data.query.Variable.Lookup
    }
    [Operation.UiNavigate]: Required<display.Link>
    [Operation.UiNotify]: Maybe.ReadonlyArray<display.Notice>
    [Operation.UiOpenAssistant]: {
      agentId: agent.Agent.Id
      records: readonly agent.KnowledgeBase.Retrieval.Record[]
    }
    [Operation.DmsMutate]: Maybe.ReadonlyArray<dms.data.Mutation>
    [Operation.ApprovalsRequest]: unknown
    [Operation.ApprovalsRespond]: unknown
  }[TOp]

  /**
   * Information needed to create a {@link Action}
   */
  export type Input = Omit<Action, 'id'>

  /**
   * Information needed to update a {@link Action}
   */
  export type Updates = Partial<Input>
}
